import React, { useState } from "react";
import { toast } from "sonner";
import {
  Link2,
  Copy,
  Trash2,
  Edit3,
  Save,
  X,
  ExternalLink,
  MousePointerClick,
  Plus,
} from "lucide-react";
import { api } from "@/lib/api";
import { t } from "@/lib/i18n";

const BUILDER_EMPTY = { slug: "", destination_url: "", label: "", stream_hint: "", source: "" };

function shortUrl(code, slug) {
  return `${window.location.origin}/l/${code}/${slug}`;
}

export default function LinkBuilder({ lang, links, code, onChanged }) {
  const [form, setForm] = useState(BUILDER_EMPTY);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(BUILDER_EMPTY);
  const [busyId, setBusyId] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setEdit = (k) => (e) => setEditForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.slug.trim() || !form.destination_url.trim()) {
      toast.error(t(lang, "required"));
      return;
    }
    setBusy(true);
    try {
      const payload = {
        slug: form.slug.trim(),
        destination_url: form.destination_url.trim(),
        label: form.label.trim(),
        stream_hint: form.stream_hint || null,
        source: form.source.trim(),
      };
      await api.post("/scout/links", payload);
      toast.success(t(lang, "link_created"));
      setForm(BUILDER_EMPTY);
      onChanged && onChanged();
    } catch (err) {
      const msg = err?.response?.data?.detail || t(lang, "link_error");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (l) => {
    setEditingId(l.id);
    setEditForm({
      slug: l.slug,
      destination_url: l.destination_url,
      label: l.label || "",
      stream_hint: l.stream_hint || "",
      source: l.source || "",
    });
  };

  const saveEdit = async (id) => {
    setBusyId(id);
    try {
      await api.patch(`/scout/links/${id}`, {
        slug: editForm.slug.trim(),
        destination_url: editForm.destination_url.trim(),
        label: editForm.label.trim(),
        stream_hint: editForm.stream_hint || null,
        source: editForm.source.trim(),
      });
      toast.success(t(lang, "link_updated"));
      setEditingId(null);
      onChanged && onChanged();
    } catch (err) {
      const msg = err?.response?.data?.detail || t(lang, "link_error");
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t(lang, "link_confirm_delete"))) return;
    setBusyId(id);
    try {
      await api.delete(`/scout/links/${id}`);
      toast.success(t(lang, "link_deleted"));
      onChanged && onChanged();
    } catch {
      toast.error(t(lang, "link_error"));
    } finally {
      setBusyId(null);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t(lang, "copied"));
    } catch {
      toast.error("Clipboard error");
    }
  };

  return (
    <section
      className="eigoo-card p-6 sm:p-7 anim-fade-up anim-delay-2"
      data-testid="link-builder-card"
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl grid place-items-center border border-[#d4a843]/40 bg-[#d4a843]/10">
          <Link2 className="w-5 h-5 text-[#f3c669]" />
        </div>
        <div>
          <h3 className="font-display text-lg sm:text-xl font-bold">
            {t(lang, "link_builder_title")}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5 max-w-md">
            {t(lang, "link_builder_sub")}
          </p>
        </div>
      </div>

      <form
        onSubmit={submit}
        className="grid grid-cols-1 sm:grid-cols-6 gap-3 items-end"
        data-testid="link-builder-form"
      >
        <div className="sm:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-2">
            {t(lang, "link_slug")}
          </span>
          <div className="flex items-stretch rounded-xl border border-[#1e3a61] bg-[#0a1628] overflow-hidden focus-within:border-[#d4a843]">
            <span className="font-mono text-[11px] text-slate-500 pl-3 pr-1 py-2.5 whitespace-nowrap">
              /l/{code}/
            </span>
            <input
              className="flex-1 bg-transparent text-slate-100 text-sm px-1 py-2.5 focus:outline-none"
              value={form.slug}
              onChange={set("slug")}
              placeholder="techops"
              data-testid="link-slug-input"
              required
            />
          </div>
        </div>
        <div className="sm:col-span-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-2">
            {t(lang, "link_destination")}
          </span>
          <input
            className="eigoo-input"
            value={form.destination_url}
            onChange={set("destination_url")}
            placeholder="https://eigooinc.net/techops"
            data-testid="link-destination-input"
            required
          />
        </div>
        <div className="sm:col-span-1">
          <button
            type="submit"
            disabled={busy}
            className="gold-btn w-full inline-flex items-center justify-center gap-2"
            data-testid="link-create-button"
          >
            <Plus className="w-4 h-4" />
            {busy ? "…" : t(lang, "link_create")}
          </button>
        </div>

        <div className="sm:col-span-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-2">
            {t(lang, "link_label")}
          </span>
          <input
            className="eigoo-input"
            value={form.label}
            onChange={set("label")}
            placeholder={t(lang, "link_label_ph")}
            data-testid="link-label-input"
          />
        </div>
        <div className="sm:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-2">
            {t(lang, "link_stream")}
          </span>
          <select
            className="eigoo-input"
            value={form.stream_hint}
            onChange={set("stream_hint")}
            data-testid="link-stream-select"
          >
            <option value="">{t(lang, "link_stream_any")}</option>
            <option value="referral">{t(lang, "stream_referral")}</option>
            <option value="amazon">{t(lang, "stream_amazon")}</option>
            <option value="marketplace">{t(lang, "stream_marketplace")}</option>
          </select>
        </div>
        <div className="sm:col-span-1">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 block mb-2">
            {t(lang, "link_source")}
          </span>
          <input
            className="eigoo-input"
            value={form.source}
            onChange={set("source")}
            placeholder="Amazon"
            data-testid="link-source-input"
          />
        </div>
      </form>

      {/* Manager */}
      <div className="mt-7">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-sm font-bold uppercase tracking-widest text-slate-300">
            {t(lang, "link_manager_title")}
          </h4>
          <div className="inline-flex items-center gap-1.5 text-xs text-slate-500">
            <MousePointerClick className="w-3.5 h-3.5" />
            {t(lang, "link_manager_hint")}
          </div>
        </div>

        {links.length === 0 ? (
          <div className="border border-dashed border-[#1e3a61] rounded-xl py-10 text-center text-slate-400 text-sm">
            {t(lang, "no_links_yet")}
          </div>
        ) : (
          <div className="space-y-2" data-testid="links-list">
            {links.map((l) => {
              const full = shortUrl(l.scout_referral_code, l.slug);
              const isEditing = editingId === l.id;
              return (
                <div
                  key={l.id}
                  className="rounded-xl border border-[#1e3a61] bg-[#0f2038]/60 p-4"
                  data-testid={`link-row-${l.id}`}
                >
                  {!isEditing ? (
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="flex-1 min-w-[220px]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="font-mono text-sm text-[#f3c669] break-all"
                            data-testid={`link-short-${l.id}`}
                          >
                            {full}
                          </span>
                          <button
                            onClick={() => copy(full)}
                            className="text-slate-400 hover:text-[#f3c669]"
                            aria-label={t(lang, "copy")}
                            data-testid={`link-copy-${l.id}`}
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <a
                          href={l.destination_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-slate-400 mt-1 hover:text-slate-200"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span className="truncate max-w-[380px]">{l.destination_url}</span>
                        </a>
                        {l.label && (
                          <div className="text-[11px] text-slate-500 mt-1 italic">{l.label}</div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <div
                          className="font-display text-xl font-extrabold tabular-nums text-white"
                          data-testid={`link-clicks-${l.id}`}
                        >
                          {l.clicks}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">
                          {t(lang, "clicks")}
                        </div>
                      </div>
                      <div className="flex gap-2 self-center">
                        <button
                          onClick={() => startEdit(l)}
                          className="ghost-btn !py-1.5 !px-2.5 inline-flex items-center gap-1 text-xs"
                          data-testid={`link-edit-${l.id}`}
                          aria-label="edit"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => remove(l.id)}
                          disabled={busyId === l.id}
                          className="ghost-btn !py-1.5 !px-2.5 inline-flex items-center gap-1 text-xs hover:!border-[#f87171] hover:!text-[#f87171]"
                          data-testid={`link-delete-${l.id}`}
                          aria-label="delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-end">
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest text-slate-400">
                          {t(lang, "link_slug")}
                        </label>
                        <input
                          className="eigoo-input mt-1"
                          value={editForm.slug}
                          onChange={setEdit("slug")}
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <label className="text-[10px] uppercase tracking-widest text-slate-400">
                          {t(lang, "link_destination")}
                        </label>
                        <input
                          className="eigoo-input mt-1"
                          value={editForm.destination_url}
                          onChange={setEdit("destination_url")}
                        />
                      </div>
                      <div className="sm:col-span-1 flex gap-1">
                        <button
                          onClick={() => saveEdit(l.id)}
                          className="gold-btn !py-2 !px-3 inline-flex items-center gap-1 text-xs w-full justify-center"
                          data-testid={`link-save-${l.id}`}
                          disabled={busyId === l.id}
                        >
                          <Save className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="ghost-btn !py-2 !px-3 inline-flex items-center gap-1 text-xs"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="sm:col-span-3">
                        <label className="text-[10px] uppercase tracking-widest text-slate-400">
                          {t(lang, "link_label")}
                        </label>
                        <input
                          className="eigoo-input mt-1"
                          value={editForm.label}
                          onChange={setEdit("label")}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest text-slate-400">
                          {t(lang, "link_stream")}
                        </label>
                        <select
                          className="eigoo-input mt-1"
                          value={editForm.stream_hint}
                          onChange={setEdit("stream_hint")}
                        >
                          <option value="">{t(lang, "link_stream_any")}</option>
                          <option value="referral">{t(lang, "stream_referral")}</option>
                          <option value="amazon">{t(lang, "stream_amazon")}</option>
                          <option value="marketplace">{t(lang, "stream_marketplace")}</option>
                        </select>
                      </div>
                      <div className="sm:col-span-1">
                        <label className="text-[10px] uppercase tracking-widest text-slate-400">
                          {t(lang, "link_source")}
                        </label>
                        <input
                          className="eigoo-input mt-1"
                          value={editForm.source}
                          onChange={setEdit("source")}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
